import React, { useEffect, useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { serverNowMs } from '../../../utils/serverTime';

/**
 * ReservationWindow — окно/панель приоритетной брони бокса после завершения мойки.
 *
 * После перехода сессии в статус `complete` бокс на время кулдауна держится за
 * клиентом (приоритетный возврат в тот же бокс). Компонент показывает обратный
 * таймер до конца кулдауна и кнопку «Оплатить и остаться», ведущую в обычный
 * флоу создания новой сессии/оплаты.
 *
 * @param {Object} props
 * @param {number|string} [props.boxNumber] - Номер бокса.
 * @param {string} [props.cooldownUntil] - ISO-дата дедлайна кулдауна (приоритетно).
 * @param {number} [props.cooldownMinutes] - Длительность кулдауна в минутах (fallback).
 * @param {string} [props.completedAt] - Момент завершения мойки (для fallback-расчёта дедлайна).
 * @param {string} [props.serviceType] - Тип услуги завершённой сессии (для предвыбора в новом флоу).
 * @param {Function} props.onPay - Колбэк перехода в флоу создания новой сессии/оплаты.
 * @param {string} [props.theme] - Тема оформления ('light' | 'dark').
 */
const ReservationWindow = ({
  boxNumber,
  cooldownUntil,
  cooldownMinutes,
  completedAt,
  serviceType,
  onPay,
  theme = 'light',
}) => {
  // Вычисляем дедлайн брони: приоритетно из cooldown_until, иначе из completedAt + cooldownMinutes.
  const deadline = useMemo(() => {
    if (cooldownUntil) {
      const d = new Date(cooldownUntil);
      if (!isNaN(d.getTime())) {
        return d.getTime();
      }
    }
    if (cooldownMinutes && completedAt) {
      const base = new Date(completedAt);
      if (!isNaN(base.getTime())) {
        return base.getTime() + cooldownMinutes * 60 * 1000;
      }
    }
    return null;
  }, [cooldownUntil, cooldownMinutes, completedAt]);

  const [secondsLeft, setSecondsLeft] = useState(() => {
    if (deadline == null) return null;
    return Math.max(0, Math.floor((deadline - serverNowMs()) / 1000));
  });

  useEffect(() => {
    if (deadline == null) {
      setSecondsLeft(null);
      return undefined;
    }

    const tick = () => {
      const remaining = Math.max(0, Math.floor((deadline - serverNowMs()) / 1000));
      setSecondsLeft(remaining);
      return remaining;
    };

    // Считаем сразу, затем каждую секунду
    if (tick() <= 0) {
      return undefined;
    }
    const intervalId = setInterval(() => {
      if (tick() <= 0) {
        clearInterval(intervalId);
      }
    }, 1000);

    return () => clearInterval(intervalId);
  }, [deadline]);

  // Если дедлайна нет вовсе — ничего не показываем.
  if (deadline == null) {
    return null;
  }

  const expired = secondsLeft == null || secondsLeft <= 0;
  const minutes = expired ? 0 : Math.floor(secondsLeft / 60);
  const seconds = expired ? 0 : secondsLeft % 60;
  const formatted = `${minutes}:${seconds.toString().padStart(2, '0')}`;

  const boxLabel = boxNumber != null && boxNumber !== '' ? `№${boxNumber}` : '';

  return (
    <Panel $theme={theme} $expired={expired}>
      <IconRow>
        <Icon>{expired ? '⌛' : '🅿️'}</Icon>
      </IconRow>

      {expired ? (
        <>
          <Title $theme={theme}>Бронь истекла</Title>
          <Text $theme={theme}>
            Время приоритетного удержания бокса {boxLabel} закончилось. Вы можете
            записаться на новую мойку в общем порядке.
          </Text>
        </>
      ) : (
        <>
          <Title $theme={theme}>
            Бокс {boxLabel} забронирован за вами ещё
          </Title>
          <Countdown $theme={theme}>{formatted}</Countdown>
          <Text $theme={theme}>Оплатите, чтобы остаться в боксе.</Text>
          {typeof onPay === 'function' && (
            <PayButton
              type="button"
              onClick={() => onPay({ serviceType })}
            >
              Оплатить и остаться
            </PayButton>
          )}
        </>
      )}
    </Panel>
  );
};

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(245, 194, 107, 0.4); }
  70% { box-shadow: 0 0 0 10px rgba(245, 194, 107, 0); }
  100% { box-shadow: 0 0 0 0 rgba(245, 194, 107, 0); }
`;

const Panel = styled.div`
  margin: 16px 0;
  padding: 20px 18px;
  border-radius: var(--h2o-r-lg, 20px);
  text-align: center;
  border: 1px solid ${({ $expired }) =>
    $expired ? 'var(--h2o-border-strong, rgba(255,255,255,0.16))' : 'var(--h2o-warning, #f5c26b)'};
  background: ${({ $expired }) =>
    $expired ? 'var(--h2o-surface-2, #1a2440)' : 'var(--h2o-warning-soft, rgba(245,194,107,0.15))'};
  box-shadow: var(--h2o-shadow-card, 0 10px 30px -12px rgba(0, 0, 0, 0.55));
  animation: ${({ $expired }) => ($expired ? 'none' : pulse)} 2.2s infinite;
`;

const IconRow = styled.div`
  display: flex;
  justify-content: center;
`;

const Icon = styled.div`
  font-size: 32px;
  line-height: 1;
  margin-bottom: 8px;
`;

const Title = styled.p`
  margin: 0 0 6px 0;
  font-family: 'Manrope', 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: ${({ $expired }) =>
    $expired ? 'var(--h2o-text, #eaf1fb)' : 'var(--h2o-warning, #f5c26b)'};
`;

const Countdown = styled.div`
  font-family: 'Manrope', 'Inter', sans-serif;
  font-size: 40px;
  font-weight: 800;
  letter-spacing: 1px;
  margin: 6px 0;
  font-variant-numeric: tabular-nums;
  color: var(--h2o-warning, #f5c26b);
`;

const Text = styled.p`
  margin: 4px 0 0 0;
  font-size: 14px;
  color: var(--h2o-text-muted, #93a3bd);
`;

const PayButton = styled.button`
  margin-top: 16px;
  width: 100%;
  padding: 14px 16px;
  font-family: 'Manrope', 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #2a1e05;
  background: var(--h2o-warning, #f5c26b);
  border: none;
  border-radius: var(--h2o-r-pill, 999px);
  cursor: pointer;
  transition: filter 0.15s ease, transform 0.15s ease;

  &:hover {
    filter: brightness(1.05);
  }

  &:active {
    transform: scale(0.98);
  }
`;

export default ReservationWindow;
